import { FormAutocompleteOption } from 'app/shared/components/form/Autocomplete';
import { AdornmentType } from 'app/shared/componentsRestyled/coreDesignSystem/form/DataFieldCDS/DataFieldCDS.constants';
import { isEqual } from 'lodash';
import { compose } from 'redux';

import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import {
  CALCULATED_AFTER_MODAL_SAVED_PLACEHOLDER,
  helocPurposeOptions,
  LoanPurposeType,
  LoanType,
  NationalHousingActSection,
  TangibleNetBenefitReasonType,
} from 'constants/loanApplication';
import {
  additionalLoanSecondMortgageOptions,
  documentVerificationAssetTypeOptions,
  documentVerificationEmploymentTypeOptions,
  documentVerificationIncomeTypeOptions,
  governmentRefinancePurposeOptions,
  incomeDocumentationTypeOptions,
  lienPriorityOptions,
  loanPurposeOptions,
  nationalHousingActSectionOptions,
  prepaymentPenaltyStructureOptions,
  programLoanTypeOptions,
  purposeOfChattelOptions,
  refinancePurposeOptions,
  tangibleNetBenefitReasonOptions,
} from 'constants/options';
import { getConditionalArray } from 'utils/data';
import {
  checkIsChattelLoanPurpose,
  checkIsGovernmentRefinancePurposeVisible,
  checkIsPurchaseFlow,
  getGovernmentRefinancePurposeFilteredOptions,
  getRefinancePurposeFilteredOptions,
} from 'utils/loanApplication';
import { fillConfigFieldsWithKeys } from 'utils/loanManagement';
import { getPendingChangeKind } from 'utils/pendingChanges';

import { PropertyAndLoanTestIds } from '../../../../PropertyAndLoan.testIds';

import {
  DownPaymentFieldName,
  DownPaymentFieldWidthPercent,
  DSCR_DECIMAL_SCALE,
  DSCR_MAX,
  PREPAYMENT_PENALTY_TERM_MAX_MONTHS,
  RATE_DECIMAL_SCALE,
} from './LoanInfo.constants';
import { loanProgramFieldNames } from './LoanProgramSection.config';

export const loanInfoConfigFields = {
  baseLoanAmount: {
    fieldId: 'baseLoanAmount',
    type: FieldType.INPUT,
    nameInModel: 'baseLoanAmount',
    inputProps: {
      type: FieldInputType.CURRENCY,
      placeholder: 'Loan Amount',
      label: 'Loan Amount',
      required: true,
      decimalScale: 0,
    },
  },
  loanPurpose: {
    fieldId: 'loanPurpose',
    type: FieldType.SELECT,
    nameInModel: 'loanPurpose',
    inputProps: {
      options: loanPurposeOptions,
      placeholder: 'Purpose',
      label: 'Purpose',
      required: true,
    },
  },
  downPayment: {
    fieldId: 'downPayment',
    type: FieldType.COMBINED,
    nameInModel: DownPaymentFieldName.AMOUNT,
    combinedFields: [
      {
        nameInModel: DownPaymentFieldName.AMOUNT,
        widthPercent: DownPaymentFieldWidthPercent.AMOUNT,
        inputProps: {
          type: FieldInputType.CURRENCY,
          adornmentType: AdornmentType.START,
          required: false,
          decimalScale: 0,
          dataTestId: PropertyAndLoanTestIds.LOAN_INFO_FORM_DOWN_PAYMENT_INPUT,
        },
      },
      {
        nameInModel: DownPaymentFieldName.PERCENT,
        noObserve: true,
        widthPercent: DownPaymentFieldWidthPercent.PERCENT,
        shouldShowRawWhileFocused: true,
        inputProps: {
          type: FieldInputType.PERCENTAGE_RESTYLED,
          adornmentType: AdornmentType.END,
          required: false,
          decimalScale: RATE_DECIMAL_SCALE,
          decimalSeparator: '.',
          fixedDecimalScale: true,
          isNumericString: true,
          dataTestId: PropertyAndLoanTestIds.LOAN_INFO_FORM_DOWN_PAYMENT_PERCENT_INPUT,
        },
      },
    ],
    inputProps: {
      label: 'Down Payment',
      required: false,
      variant: 'outlined',
      dataTestId: PropertyAndLoanTestIds.LOAN_INFO_FORM_DOWN_PAYMENT_FIELD,
    },
  },
  buyDown: {
    fieldId: 'buyDown',
    type: FieldType.SELECT,
    nameInModel: 'buyDown',
    inputProps: {
      options: [],
      OptionContent: FormAutocompleteOption,
      placeholder: 'Buy Down',
      label: 'Buy Down',
    },
  },
  totalLoanAmount: {
    fieldId: 'totalLoanAmount',
    type: FieldType.INPUT,
    nameInModel: 'totalLoanAmount',
    noValidate: true,
    inputProps: {
      type: FieldInputType.CURRENCY,
      placeholder: 'Total Loan Amount',
      label: 'Total Loan Amount',
      required: true,
      disabled: true,
    },
  },
  refinancePurpose: {
    fieldId: 'refinancePurpose',
    type: FieldType.SELECT,
    nameInModel: 'refinancePurpose',
    inputProps: {
      options: refinancePurposeOptions,
      placeholder: 'Purpose of Refinance',
      label: 'Purpose of Refinance',
      required: true,
    },
  },
  purposeOfChattel: {
    fieldId: 'purposeOfChattel',
    type: FieldType.SELECT,
    nameInModel: 'purposeOfChattel',
    inputProps: {
      options: purposeOfChattelOptions,
      placeholder: 'Chattel Purpose',
      label: 'Chattel Purpose',
      required: true,
    },
  },
  helocPurpose: {
    fieldId: 'helocPurpose',
    type: FieldType.SELECT,
    nameInModel: 'helocPurpose',
    inputProps: {
      options: helocPurposeOptions,
      placeholder: 'Additional Loan Purpose',
      label: 'Additional Loan Purpose',
      required: true,
    },
  },
  additionalLoanSecondMortgageType: {
    fieldId: 'additionalLoanSecondMortgageType',
    type: FieldType.SELECT,
    nameInModel: 'additionalLoanSecondMortgageType',
    inputProps: {
      options: additionalLoanSecondMortgageOptions,
      placeholder: 'Second Mortgage Type',
      label: 'Second Mortgage Type',
      required: true,
    },
  },
  additionalLoanDrawAmount: {
    fieldId: 'additionalLoanDrawAmount',
    type: FieldType.INPUT,
    nameInModel: 'additionalLoanDrawAmount',
    inputProps: {
      type: FieldInputType.CURRENCY,
      placeholder: 'Draw Amount',
      label: 'Draw Amount',
      required: true,
      decimalScale: 0,
    },
  },
  lienPriority: {
    fieldId: 'lienPriority',
    type: FieldType.SELECT,
    nameInModel: 'lienPriority',
    inputProps: {
      staticOptions: lienPriorityOptions,
      options: lienPriorityOptions,
      placeholder: 'Lien Priority',
      label: 'Lien Priority',
      required: true,
    },
  },
  cashOutAmount: {
    fieldId: 'cashOutAmount',
    type: FieldType.INPUT,
    nameInModel: 'cashOutAmount',
    inputProps: {
      type: FieldInputType.CURRENCY,
      placeholder: 'Requested Cash Out Amount',
      label: 'Requested Cash Out Amount',
      required: true,
      disabled: false,
      decimalScale: 0,
    },
  },
  // for appropriate schema in config
  isEscrowed: {
    fieldId: 'isEscrowed',
    nameInModel: 'escrowSettings.isEscrowed',
  },
  isTaxesEscrowed: {
    fieldId: 'isTaxesEscrowed',
    nameInModel: 'escrowSettings.taxesEscrowSettings.isEscrowed',
  },
  isInsuranceEscrowed: {
    fieldId: 'isInsuranceEscrowed',
    nameInModel: 'escrowSettings.insuranceEscrowSettings[0].isEscrowed',
  },
  areaMedianIncome: {
    fieldId: 'areaMedianIncome',
    type: FieldType.INPUT,
    nameInModel: 'areaMedianIncome',
    inputProps: {
      type: FieldInputType.CURRENCY,
      placeholder: 'AMI',
      label: 'AMI',
      required: true,
      disabled: true,
      decimalScale: 0,
    },
  },
  housingFinanceAgencyId: {
    fieldId: 'hfaInfo',
    type: FieldType.SELECT,
    nameInModel: 'housingFinanceAgencyId',
    inputProps: {
      label: 'Housing Finance Agency (HFA)',
      required: false,
      disabled: false,
      options: [],
      withSortedOptions: true,
    },
  },
  housingFinanceAgencyOtherDescription: {
    fieldId: 'hfaInfo',
    type: FieldType.INPUT,
    nameInModel: 'housingFinanceAgencyOtherDescription',
    inputProps: {
      label: 'Housing Finance Agency Name',
      placeholder: 'Housing Finance Agency Name',
      required: true,
      disabled: false,
    },
  },
  investorCodes: {
    fieldId: 'investorCodes',
    type: FieldType.SELECT,
    nameInModel: 'investorCodes',
    inputProps: {
      options: [],
      placeholder: 'Investor Code',
      label: 'Investor Code',
      required: false,
      multiple: true,
      variant: 'outlined',
      isOptionEqualToValue: (option, value) => isEqual(option, value),
    },
  },
  leadSource: {
    fieldId: 'leadSource',
    type: FieldType.SELECT,
    nameInModel: 'leadSource',
    inputProps: {
      options: [],
      placeholder: 'Choose Lead Source',
      label: 'Lead Source',
      required: false,
      variant: 'outlined',
    },
  },
  loanProgramIdentifiers: {
    fieldId: 'loanProgramIdentifiers',
    type: FieldType.SELECT,
    nameInModel: 'loanProgramIdentifiers',
    inputProps: {
      options: [],
      placeholder: 'Loan Program Identifier',
      label: 'Loan Program Identifier',
      required: false,
      multiple: true,
      variant: 'outlined',
      optionKey: 'value',
      isOptionEqualToValue: (option, value) => isEqual(option, value),
    },
  },
  usdaEligible: {
    fieldId: 'usdaEligible',
    type: FieldType.CHECKBOX,
    nameInModel: 'usdaEligible',
    inputProps: {
      label: 'USDA Eligible',
      required: false,
    },
  },
  loanWorkflow: {
    fieldId: 'loanWorkflow',
    type: FieldType.INPUT,
    nameInModel: 'loanWorkflow',
    inputProps: {
      label: 'Loan Workflow',
      required: false,
      disabled: true,
    },
  },
  nationalHousingActSection: {
    type: FieldType.SELECT,
    fieldId: 'nationalHousingActSection',
    nameInModel: 'nationalHousingActSection',
    inputProps: {
      options: nationalHousingActSectionOptions,
      placeholder: 'National Housing Act Section',
      label: 'National Housing Act Section',
      required: false,
      variant: 'outlined',
    },
  },
  agencyCaseNumber: {
    fieldId: 'agencyCaseNumber',
    type: FieldType.INPUT,
    nameInModel: 'agencyCaseNumber',
    inputProps: {
      type: FieldInputType.TEXT_SPECIFIC_PATTERN,
      maxLength: 10,
      patternRegExp: /^[0-9]{10}$/,
      validationMessage: 'Agency Case Number must contain 10 characters',
      label: 'Agency Case Number',
      placeholder: 'Agency Case Number',
      required: false,
      disabled: false,
      allowNegative: false,
      decimalSeparator: false,
      allowLeadingZeros: true,
      isNumericString: true,
      variant: 'outlined',
    },
  },
  agencyCaseNumberIssueDate: {
    fieldId: 'agencyCaseNumberIssueDate',
    type: FieldType.DATE,
    nameInModel: 'agencyCaseNumberIssueDate',
    inputProps: {
      label: 'Agency Case Number Issue Date',
      required: false,
      disabled: false,
      variant: 'outlined',
    },
  },
  vaTotalEntitlement: {
    fieldId: 'vaTotalEntitlement',
    type: FieldType.INPUT,
    nameInModel: 'vaTotalEntitlement',
    inputProps: {
      type: FieldInputType.CURRENCY,
      label: 'VA Total Entitlement:',
      placeholder: 'VA Total Entitlement:',
      required: false,
      disabled: true,
    },
  },
  minimumResidualIncome: {
    fieldId: 'minimumResidualIncome',
    type: FieldType.INPUT,
    nameInModel: 'minimumResidualIncome',
    inputProps: {
      type: FieldInputType.CURRENCY,
      placeholder: 'Minimum Residual Income',
      label: 'Minimum Residual Income',
      required: false,
      disabled: true,
      allowNegative: true,
      dataTestId: PropertyAndLoanTestIds.LOAN_INFO_FORM_MINIMUM_RESIDUAL_INCOME_INPUT,
      'data-test-value': 10000,
    },
  },
  tangibleNetBenefitReason: {
    fieldId: 'tangibleNetBenefitReason',
    type: FieldType.SELECT,
    nameInModel: 'tangibleNetBenefitReason',
    inputProps: {
      options: tangibleNetBenefitReasonOptions,
      placeholder: 'Tangible Net Benefit Reason',
      label: 'Tangible Net Benefit Reason',
      required: false,
    },
  },
  tangibleNetBenefitCurrentInterestRate: {
    fieldId: 'tangibleNetBenefitCurrentInterestRate',
    type: FieldType.INPUT,
    nameInModel: 'tangibleNetBenefitCurrentInterestRate',
    inputProps: {
      type: FieldInputType.PERCENTAGE_RESTYLED,
      placeholder: 'Current Interest Rate',
      label: 'Current Interest Rate',
      required: false,
      decimalScale: RATE_DECIMAL_SCALE,
    },
  },
  tangibleNetBenefitCurrentAmortizationPeriodMonths: {
    fieldId: 'tangibleNetBenefitCurrentAmortizationPeriodMonths',
    type: FieldType.INPUT,
    nameInModel: 'tangibleNetBenefitCurrentAmortizationPeriodMonths',
    inputProps: {
      type: FieldInputType.INT,
      placeholder: 'Current Amortization Period (Months)',
      label: 'Current Amortization Period (Months)',
      required: false,
    },
  },
  tangibleNetBenefitDescription: {
    fieldId: 'tangibleNetBenefitDescription',
    type: FieldType.INPUT,
    nameInModel: 'tangibleNetBenefitDescription',
    inputProps: {
      placeholder: 'Describe Tangible Net Benefit',
      label: 'Describe Tangible Net Benefit',
      required: true,
      maxLength: 150,
    },
  },
  governmentRefinancePurpose: {
    fieldId: 'governmentRefinancePurpose',
    type: FieldType.SELECT,
    nameInModel: 'governmentRefinancePurpose',
    inputProps: {
      options: governmentRefinancePurposeOptions,
      label: 'Government Refinance Purpose',
      disabled: false,
      required: true,
      variant: 'outlined',
    },
  },
  hudFieldOfficeCode: {
    fieldId: 'hudFieldOfficeCode',
    type: FieldType.INPUT,
    nameInModel: 'hudFieldOfficeCode',
    inputProps: {
      label: 'HUD Field Office Code',
      required: false,
      disabled: true,
    },
  },
  hudFieldOfficeName: {
    fieldId: 'hudFieldOfficeName',
    type: FieldType.INPUT,
    nameInModel: 'hudFieldOfficeName',
    inputProps: {
      label: 'HUD Field Office Name',
      required: false,
      disabled: true,
    },
  },
  hudHocCode: {
    fieldId: 'hudHocCode',
    type: FieldType.INPUT,
    nameInModel: 'hudHocCode',
    inputProps: {
      label: 'HUD HOC Code',
      required: false,
      disabled: true,
    },
  },
  hudHocName: {
    fieldId: 'hudHocName',
    type: FieldType.INPUT,
    nameInModel: 'hudHocName',
    inputProps: {
      label: 'HUD HOC Name',
      required: false,
      disabled: true,
    },
  },
  fhaInspectorIdentifier: {
    fieldId: 'fhaInspectorIdentifier',
    type: FieldType.INPUT,
    nameInModel: 'fhaInspectorIdentifier',
    inputProps: {
      label: 'HUD Inspector ID',
      required: false,
      disabled: true,
    },
  },

  fha203kConsultantId: {
    fieldId: 'fha203kConsultantId',
    type: FieldType.INPUT,
    nameInModel: 'fha203kConsultantId',
    inputProps: {
      label: '203k Consultant Identifier',
      placeholder: '203k Consultant Identifier',
      required: false,
    },
  },
  fhaBorrowerCertification: {
    fieldId: 'fhaBorrowerCertification',
    type: FieldType.CHECKBOX,
    nameInModel: 'fhaBorrowerCertification',
    inputProps: {
      label: 'Borrower(s) have certified FHA program terms',
      required: false,
      wrapperStyles: { display: 'flex', alignItems: 'flex-end' },
      customStyles: { '& .checkbox': { marginBottom: 0 } },
    },
  },

  documentVerificationAssetType: {
    fieldId: 'documentVerificationAssetType',
    type: FieldType.SELECT,
    nameInModel: 'documentVerificationAssetType',
    inputProps: {
      options: documentVerificationAssetTypeOptions,
      placeholder: 'Asset',
      label: 'Asset',
      required: false,
      disabled: true,
      disableClearable: true,
      variant: 'outlined',
    },
  },
  documentVerificationEmploymentType: {
    fieldId: 'documentVerificationEmploymentType',
    type: FieldType.SELECT,
    nameInModel: 'documentVerificationEmploymentType',
    inputProps: {
      options: documentVerificationEmploymentTypeOptions,
      placeholder: 'Employment',
      label: 'Employment',
      required: false,
      disabled: true,
      disableClearable: true,
      variant: 'outlined',
    },
  },
  documentVerificationIncomeType: {
    fieldId: 'documentVerificationIncomeType',
    type: FieldType.SELECT,
    nameInModel: 'documentVerificationIncomeType',
    inputProps: {
      options: documentVerificationIncomeTypeOptions,
      placeholder: 'Income',
      label: 'Income',
      required: false,
      disabled: true,
      disableClearable: true,
      variant: 'outlined',
    },
  },
  incomeDocumentationType: {
    fieldId: 'incomeDocumentationType',
    type: FieldType.SELECT,
    nameInModel: 'incomeDocumentationType',
    inputProps: {
      options: incomeDocumentationTypeOptions,
      placeholder: 'Income Documentation',
      label: 'Income Documentation',
      required: false,
      disabled: false,
      withSortedOptions: false,
      variant: 'outlined',
      dataTestId: PropertyAndLoanTestIds.LOAN_INFO_FORM_INCOME_DOCUMENTATION_SELECT,
    },
  },
  debtServiceCoverageRatio: {
    fieldId: 'debtServiceCoverageRatio',
    type: FieldType.INPUT,
    nameInModel: 'debtServiceCoverageRatio',
    inputProps: {
      type: FieldInputType.FLOAT,
      decimalScale: DSCR_DECIMAL_SCALE,
      minValue: 0,
      maxValue: DSCR_MAX,
      placeholder: 'Calculated from rent',
      label: 'DSCR',
      required: false,
      disabled: false,
      variant: 'outlined',
      dataTestId: PropertyAndLoanTestIds.LOAN_INFO_FORM_DSCR_INPUT,
    },
  },
  prepaymentPenaltyTermMonths: {
    fieldId: 'prepaymentPenaltyTermMonths',
    type: FieldType.INPUT,
    nameInModel: 'prepaymentPenaltyTermMonths',
    inputProps: {
      type: FieldInputType.INT,
      minValue: 0,
      maxValue: PREPAYMENT_PENALTY_TERM_MAX_MONTHS,
      placeholder: 'Prepayment Penalty Term (Months)',
      label: 'Prepayment Penalty Term (Months)',
      required: false,
      disabled: false,
      variant: 'outlined',
      dataTestId: PropertyAndLoanTestIds.LOAN_INFO_FORM_PREPAYMENT_PENALTY_TERM_INPUT,
    },
  },
  prepaymentPenaltyStructure: {
    fieldId: 'prepaymentPenaltyStructure',
    type: FieldType.SELECT,
    nameInModel: 'prepaymentPenaltyStructure',
    inputProps: {
      options: prepaymentPenaltyStructureOptions,
      placeholder: 'Prepayment Penalty Structure',
      label: 'Prepayment Penalty Structure',
      required: false,
      disabled: false,
      withSortedOptions: false,
      variant: 'outlined',
      dataTestId: PropertyAndLoanTestIds.LOAN_INFO_FORM_PREPAYMENT_PENALTY_STRUCTURE_SELECT,
    },
  },
};

// LEN-127: a chattel sales contract derives the loan amount to the cent (sales price + sales tax +
// MHIT - down payment); the financed fees and prepaids on top make the Total Loan Amount, the note
// amount. A whole-dollar input would round the derived amount and save it back as an override.
// LEN-205: a chattel refinance derives its base the same way, from the home-lien payoff.
const chattelSalesContractLoanInfoFields = {
  baseLoanAmount: {
    ...loanInfoConfigFields.baseLoanAmount,
    inputProps: {
      ...loanInfoConfigFields.baseLoanAmount.inputProps,
      placeholder: 'Base Loan Amount',
      label: 'Base Loan Amount',
      decimalScale: 2,
    },
  },
  downPayment: {
    ...loanInfoConfigFields.downPayment,
    combinedFields: loanInfoConfigFields.downPayment.combinedFields.map(field =>
      field.nameInModel === DownPaymentFieldName.AMOUNT
        ? { ...field, inputProps: { ...field.inputProps, decimalScale: 2 } }
        : field,
    ),
  },
};

export const getLoanDetailsConfig = compose(
  fillConfigFieldsWithKeys,
  ({
    loanPurpose,
    shouldShowTotalLoanAmount,
    mortgageLienPriorityOptions,
    mortgageType,
    insuranceEscrowIds,
    isRefinanceCashOutSubflow,
    shouldRecalculateCashOutAmount,
    isTangibleNetBenefitVisible,
    tangibleNetBenefitReason,
    buyDownOptions,
    isLoanLocked,
    isChattelSalesContract = false,
    isChattelRefinance = false,
  }) => {
    const isRefinance = loanPurpose === LoanPurposeType.REFINANCE;
    const isAdditionalLoan = loanPurpose === LoanPurposeType.ADDITIONAL_LOAN;
    const isPurchase = checkIsPurchaseFlow(loanPurpose);
    const isChattelLoan = checkIsChattelLoanPurpose(loanPurpose);
    const isChattelFinanced = isChattelSalesContract || isChattelRefinance;
    const isMortgageTypeVA = mortgageType === LoanType.VA;

    const isHelocSecondMortgage = mortgageType === LoanType.HELOC;

    const isTangibleNetBenefitLowerInterestRate =
      tangibleNetBenefitReason === TangibleNetBenefitReasonType.LOWER_INTEREST_RATE;
    const isTangibleNetBenefitAmortizationPeriod =
      tangibleNetBenefitReason ===
      TangibleNetBenefitReasonType.BENEFICIAL_CHANGE_IN_AMORTIZATION_PERIOD;
    const isTangibleNetBenefitOther =
      tangibleNetBenefitReason === TangibleNetBenefitReasonType.OTHER;

    return {
      groups: [
        {
          type: FieldType.GROUP,
          fields: [
            ...getConditionalArray(isAdditionalLoan, () => [
              loanInfoConfigFields.helocPurpose,
              ...getConditionalArray(isHelocSecondMortgage, [
                loanInfoConfigFields.additionalLoanDrawAmount,
              ]),
            ]),
            {
              ...loanInfoConfigFields.lienPriority,
              inputProps: {
                ...loanInfoConfigFields.lienPriority.inputProps,
                options: mortgageLienPriorityOptions,
              },
            },
            isChattelFinanced
              ? chattelSalesContractLoanInfoFields.baseLoanAmount
              : loanInfoConfigFields.baseLoanAmount,
            {
              ...loanInfoConfigFields.loanPurpose,
              inputProps: {
                ...loanInfoConfigFields.loanPurpose.inputProps,
                disabled: isLoanLocked,
              },
            },
            ...getConditionalArray(isChattelLoan, [loanInfoConfigFields.purposeOfChattel]),
            ...getConditionalArray(isPurchase && !isChattelSalesContract, [
              loanInfoConfigFields.downPayment,
            ]),
            ...getConditionalArray(isChattelSalesContract, [
              chattelSalesContractLoanInfoFields.downPayment,
            ]),
            {
              ...loanInfoConfigFields.buyDown,
              inputProps: {
                ...loanInfoConfigFields.buyDown.inputProps,
                options: buyDownOptions,
              },
            },
            ...getConditionalArray(isRefinance, () => [
              {
                ...loanInfoConfigFields.refinancePurpose,
                inputProps: {
                  ...loanInfoConfigFields.refinancePurpose.inputProps,
                  options: getRefinancePurposeFilteredOptions(mortgageType),
                },
              },
            ]),
            ...getConditionalArray(isRefinance && isRefinanceCashOutSubflow, [
              !shouldRecalculateCashOutAmount
                ? loanInfoConfigFields.cashOutAmount
                : {
                    ...loanInfoConfigFields.cashOutAmount,
                    inputProps: {
                      ...loanInfoConfigFields.cashOutAmount.inputProps,
                      placeholder: CALCULATED_AFTER_MODAL_SAVED_PLACEHOLDER,
                    },
                  },
            ]),
            ...getConditionalArray(isTangibleNetBenefitVisible, () => [
              loanInfoConfigFields.tangibleNetBenefitReason,
              ...getConditionalArray(isTangibleNetBenefitLowerInterestRate, [
                loanInfoConfigFields.tangibleNetBenefitCurrentInterestRate,
              ]),
              ...getConditionalArray(isTangibleNetBenefitAmortizationPeriod, [
                loanInfoConfigFields.tangibleNetBenefitCurrentAmortizationPeriodMonths,
              ]),
              ...getConditionalArray(isTangibleNetBenefitOther, [
                loanInfoConfigFields.tangibleNetBenefitDescription,
              ]),
            ]),
            ...getConditionalArray(shouldShowTotalLoanAmount || isChattelFinanced, [
              loanInfoConfigFields.totalLoanAmount,
            ]),
            ...getConditionalArray(isAdditionalLoan, [
              {
                ...loanInfoConfigFields.additionalLoanSecondMortgageType,
                noValidate: true,
                inputProps: {
                  ...loanInfoConfigFields.additionalLoanSecondMortgageType.inputProps,
                  containerClassName: 'hidden-imp',
                },
              },
            ]),
            ...getConditionalArray(isMortgageTypeVA, [loanInfoConfigFields.vaTotalEntitlement]),
            loanInfoConfigFields.isEscrowed,
            loanInfoConfigFields.isTaxesEscrowed,
            ...insuranceEscrowIds.map(id => ({
              ...loanInfoConfigFields.isInsuranceEscrowed,
              nameInModel: loanInfoConfigFields.isInsuranceEscrowed.nameInModel.replace(
                '[0]',
                `[${id}]`,
              ),
            })),
          ],
          key: `loan-details`,
        },
      ],
    };
  },
);

const checkIs203kSection = nationalHousingActSection =>
  [
    NationalHousingActSection.SECTION_203K_FHA_REHABILITATION_LOAN_INSURANCE,
    NationalHousingActSection.SECTION_203K251_PURCHASE_REHAB_ARM,
  ].includes(nationalHousingActSection);

export const getAdditionalLoanInformationConfig = compose(
  fillConfigFieldsWithKeys,
  ({
    programIdentifiersOptions,
    investorFeatureCodesOptions,
    leadSourcesOptions,
    isLoanNoPpeEngineType,
    loanWorkflow,
    mortgageType,
    loanPurpose,
    housingFinanceAgenciesOptions,
    isOtherHousingFinanceAgency,
    isLoanLocked,
    nationalHousingActSection,
  }) => {
    const isMortgageTypeFHA = mortgageType === LoanType.FHA;
    const isMortgageTypeVA = mortgageType === LoanType.VA;

    const isGovernmentRefinancePurpose = checkIsGovernmentRefinancePurposeVisible(
      mortgageType,
      loanPurpose,
    );

    return {
      groups: [
        {
          type: FieldType.GROUP,
          fields: [
            loanInfoConfigFields.areaMedianIncome,
            ...getConditionalArray(isMortgageTypeFHA || isMortgageTypeVA, [
              loanInfoConfigFields.agencyCaseNumber,
              {
                ...loanInfoConfigFields.agencyCaseNumberIssueDate,
                inputProps: {
                  ...loanInfoConfigFields.agencyCaseNumberIssueDate.inputProps,
                  disabled: isLoanLocked,
                },
              },
            ]),
            ...getConditionalArray(isGovernmentRefinancePurpose, () => [
              {
                ...loanInfoConfigFields.governmentRefinancePurpose,
                inputProps: {
                  ...loanInfoConfigFields.governmentRefinancePurpose.inputProps,
                  options: getGovernmentRefinancePurposeFilteredOptions(mortgageType),
                },
              },
            ]),
            ...getConditionalArray(isMortgageTypeFHA, () => [
              {
                ...loanInfoConfigFields.housingFinanceAgencyId,
                inputProps: {
                  ...loanInfoConfigFields.housingFinanceAgencyId.inputProps,
                  options: housingFinanceAgenciesOptions,
                },
              },
              ...getConditionalArray(isOtherHousingFinanceAgency, [
                loanInfoConfigFields.housingFinanceAgencyOtherDescription,
              ]),
            ]),
            {
              ...loanInfoConfigFields.investorCodes,
              inputProps: {
                ...loanInfoConfigFields.investorCodes.inputProps,
                options: investorFeatureCodesOptions,
              },
            },
            ...getConditionalArray(!isLoanNoPpeEngineType, [
              {
                ...loanInfoConfigFields.leadSource,
                inputProps: {
                  ...loanInfoConfigFields.leadSource.inputProps,
                  options: leadSourcesOptions,
                },
              },
            ]),
            {
              ...loanInfoConfigFields.loanProgramIdentifiers,
              inputProps: {
                ...loanInfoConfigFields.loanProgramIdentifiers.inputProps,
                options: programIdentifiersOptions,
              },
            },
            ...getConditionalArray(!!loanWorkflow, [loanInfoConfigFields.loanWorkflow]),
            ...getConditionalArray(isMortgageTypeVA, [loanInfoConfigFields.minimumResidualIncome]),
            ...getConditionalArray(isMortgageTypeFHA, () => [
              loanInfoConfigFields.nationalHousingActSection,
              ...getConditionalArray(checkIs203kSection(nationalHousingActSection), [
                loanInfoConfigFields.fha203kConsultantId,
              ]),
              loanInfoConfigFields.fhaBorrowerCertification,
              loanInfoConfigFields.hudFieldOfficeCode,
              loanInfoConfigFields.hudFieldOfficeName,
              loanInfoConfigFields.hudHocCode,
              loanInfoConfigFields.hudHocName,
              loanInfoConfigFields.fhaInspectorIdentifier,
            ]),
            // loanInfoConfigFields.usdaEligible,
          ],
          key: `additional-loan-information`,
          groupTitle: {
            label: 'Additional Loan Information',
          },
        },
      ],
    };
  },
);

export const getDocumentVerificationTypesConfig = compose(fillConfigFieldsWithKeys, () => {
  return {
    groups: [
      {
        type: FieldType.GROUP,
        fields: [
          loanInfoConfigFields.documentVerificationAssetType,
          loanInfoConfigFields.documentVerificationEmploymentType,
          loanInfoConfigFields.documentVerificationIncomeType,
        ],
        key: `document-verification-types`,
        groupTitle: {
          label: 'Document Verification Types',
        },
      },
    ],
  };
});

export const getNonQmLoanInfoConfig = compose(
  fillConfigFieldsWithKeys,
  ({ isPrepaymentPenaltyVisible }) => ({
    groups: [
      {
        type: FieldType.GROUP,
        fields: [
          loanInfoConfigFields.incomeDocumentationType,
          loanInfoConfigFields.debtServiceCoverageRatio,
          ...getConditionalArray(isPrepaymentPenaltyVisible, [
            loanInfoConfigFields.prepaymentPenaltyTermMonths,
            loanInfoConfigFields.prepaymentPenaltyStructure,
          ]),
        ],
        key: `non-qm-loan-info`,
        groupTitle: {
          label: 'Non-QM',
        },
      },
    ],
  }),
);

export const getProgramDetailsRows = ({ data, pendingChanges = {} }) => [
  {
    label: 'Program',
    value: data.programName || '-',
    changeKind: getPendingChangeKind(pendingChanges.programName),
  },
  {
    label: 'Interest Rate',
    value: data.rate ? `${data.rate.toFixed(RATE_DECIMAL_SCALE)}%` : '-',
    changeKind: getPendingChangeKind(pendingChanges.interestRate),
  },
  {
    label: 'Loan Type',
    value: programLoanTypeOptions.find(({ value }) => data.mortgageType === value)?.name || '-',
    changeKind: getPendingChangeKind(pendingChanges.loanType),
  },
  {
    label: 'Loan Term',
    value: data.loanPeriod || '-',
    changeKind: getPendingChangeKind(pendingChanges.loanTerm),
  },
  {
    label: 'Amortization Type',
    value: data.amortizationType || '-',
    changeKind: getPendingChangeKind(pendingChanges.amortizationType),
  },
  {
    label: 'Amortization Term',
    value: data.amortizationTerm || '-',
    changeKind: getPendingChangeKind(pendingChanges.amortTerm),
  },
];

const programDetailsPendingChangesFieldNames = [
  'programName',
  'interestRate',
  'loanType',
  'loanTerm',
  'amortizationType',
  'amortTerm',
];

const escrowPendingChangesFieldNames = ['escrowSettings'];

export const loanInfoSectionFieldsKeys = [
  ...Object.keys(loanInfoConfigFields),
  ...programDetailsPendingChangesFieldNames,
  ...escrowPendingChangesFieldNames,
  ...loanProgramFieldNames,
];
